import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Upload, RefreshCw, Eye, Trash2, AlertTriangle, X,
  CheckCircle, Copy, Tag
} from 'lucide-react';
import useAuthStore from '../stores/authStore';
import '../styles/v2/pages/entry-fleet.css';

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

const EntryApkList = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [apks, setApks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const fetchApks = useCallback(async () => {
    try {
      const url = '/api/platform/entry/apks' + (includeInactive ? '?includeInactive=true' : '');
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setApks(data.apks || []);
      } else if (res.status === 403) {
        setApks([]);
      }
    } catch (e) {
      console.error('Fetch APKs error:', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [includeInactive]);

  useEffect(() => { fetchApks(); }, [fetchApks]);

  if (!user?.isPlatformAdmin) {
    return (
      <div className="entry-fleet">
        <div className="entry-detail-empty">
          <AlertTriangle size={32} />
          <h3>Platform Admin Required</h3>
          <p style={{ color: 'var(--text-tertiary)' }}>
            APK management is restricted to Platform Admins.
          </p>
        </div>
      </div>
    );
  }

  const inactiveCount = apks.filter(a => !a.isActive).length;
  const visible = includeInactive ? apks : apks.filter(a => a.isActive);

  return (
    <div className="entry-fleet">
      <div className="entry-header">
        <div className="entry-header-left">
          <h1><Package size={24} /> Entry APK Updates</h1>
          <span className="entry-header-sub">Platform Admin · Signed APK builds for the Entry tablet</span>
        </div>
        <div className="entry-header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => { setRefreshing(true); fetchApks(); }}
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            <Upload size={16} /> Upload APK
          </button>
        </div>
      </div>

      {/* Inactive toggle */}
      {(inactiveCount > 0 || includeInactive) && (
        <div style={{ padding: '12px 20px' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={e => setIncludeInactive(e.target.checked)}
            />
            Show inactive ({inactiveCount})
          </label>
        </div>
      )}

      {loading ? (
        <div className="entry-loading"><RefreshCw size={24} className="spin" /><span>Loading APKs...</span></div>
      ) : visible.length === 0 ? (
        <div className="entry-detail-empty-section" style={{ margin: '24px' }}>
          <Package size={32} style={{ opacity: 0.3 }} />
          <p>No APKs uploaded yet. Click "Upload APK" to get started.</p>
        </div>
      ) : (
        <div style={{ padding: '0 20px 20px', overflowX: 'auto' }}>
          <table className="apk-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Build</th>
                <th>Size</th>
                <th>SHA-256</th>
                <th>Uploaded</th>
                <th>By</th>
                <th>Pushes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(apk => (
                <tr
                  key={apk.id}
                  className={'apk-row' + (apk.isActive ? '' : ' inactive')}
                  onClick={() => navigate('/settings/entry/apks/' + apk.id)}
                >
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{apk.version}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>vc{apk.versionCode}</div>
                  </td>
                  <td>
                    <span className={'apk-pill apk-pill-' + apk.buildType}>{apk.buildType}</span>
                  </td>
                  <td>{formatBytes(apk.sizeBytes)}</td>
                  <td title={apk.sha256} className="mono" style={{ fontSize: '11px' }}>
                    {apk.sha256.slice(0, 12)}…
                  </td>
                  <td style={{ fontSize: '12px' }}>{formatDate(apk.uploadedAt)}</td>
                  <td style={{ fontSize: '12px' }}>{apk.uploader?.name || apk.uploader?.email || '—'}</td>
                  <td>{apk._count?.pushes ?? 0}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => { e.stopPropagation(); navigate('/settings/entry/apks/' + apk.id); }}
                    >
                      <Eye size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onSuccess={(apk) => {
            setShowUpload(false);
            fetchApks();
            navigate('/settings/entry/apks/' + apk.id);
          }}
        />
      )}

      <style>{`
        .apk-table { width: 100%; border-collapse: collapse; }
        .apk-table th { text-align: left; padding: 10px 12px; color: var(--text-tertiary); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid rgba(255,255,255,0.08); }
        .apk-table td { padding: 12px; border-bottom: 1px solid rgba(255,255,255,0.06); color: var(--text-secondary); font-size: 13px; }
        .apk-row { cursor: pointer; transition: background 120ms; }
        .apk-row:hover { background: rgba(255,255,255,0.03); }
        .apk-row.inactive td { opacity: 0.55; }
        .apk-pill { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.3px; }
        .apk-pill-release { background: rgba(34, 197, 94, 0.15); color: #22c55e; }
        .apk-pill-debug { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
        .mono { font-family: ui-monospace, monospace; }
      `}</style>
    </div>
  );
};

function UploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [buildType, setBuildType] = useState('release');
  const [changelog, setChangelog] = useState('');
  const [gitCommit, setGitCommit] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const submit = async () => {
    setError(null);

    if (!file) { setError('Select an APK file'); return; }
    if (!version.trim()) { setError('Version required'); return; }
    const vc = parseInt(versionCode, 10);
    if (!Number.isInteger(vc) || vc < 0) { setError('versionCode must be a non-negative integer'); return; }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('version', version.trim());
      fd.append('versionCode', String(vc));
      fd.append('buildType', buildType);
      if (changelog.trim()) fd.append('changelog', changelog.trim());
      if (gitCommit.trim()) fd.append('gitCommit', gitCommit.trim());

      const res = await fetch('/api/platform/entry/apks', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || ('Upload failed (HTTP ' + res.status + ')'));
        setUploading(false);
        return;
      }
      onSuccess(data.apk);
    } catch (e) {
      setError(e.message || 'Network error');
      setUploading(false);
    }
  };

  return (
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="platform-modal-header">
          <h3><Upload size={18} /> Upload APK</h3>
          <button onClick={onClose} disabled={uploading}>
            <X size={18} />
          </button>
        </div>
        <div className="platform-modal-body" style={{ display: 'grid', gap: '14px' }}>
          {error && (
            <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', padding: '10px 12px', color: '#ef4444', fontSize: '13px' }}>
              <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              {error}
            </div>
          )}

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>APK File <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={e => setFile(e.target.files?.[0] || null)}
              disabled={uploading}
              style={{ color: 'var(--text-primary)' }}
            />
            {file && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {file.name} · {formatBytes(file.size)}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Version <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                value={version}
                onChange={e => setVersion(e.target.value)}
                placeholder="1.1.1-platform-signed"
                disabled={uploading}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Version Code <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="number"
                min="0"
                value={versionCode}
                onChange={e => setVersionCode(e.target.value)}
                placeholder="3"
                disabled={uploading}
                style={inputStyle}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Build Type <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', gap: '12px' }}>
              {['release', 'debug'].map(t => (
                <label key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="buildType"
                    value={t}
                    checked={buildType === t}
                    onChange={() => setBuildType(t)}
                    disabled={uploading}
                  />
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Changelog (markdown, optional)</label>
            <textarea
              rows={5}
              value={changelog}
              onChange={e => setChangelog(e.target.value)}
              placeholder="- New silent install via PackageInstaller&#10;- Fixed camera reinit on reboot"
              disabled={uploading}
              style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', resize: 'vertical' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>Git Commit (optional)</label>
            <input
              type="text"
              value={gitCommit}
              onChange={e => setGitCommit(e.target.value)}
              placeholder="2cd78e5"
              disabled={uploading}
              style={inputStyle}
            />
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
              First 7 chars of the commit SHA the APK was built from (optional, for traceability).
            </div>
          </div>
        </div>
        <div className="platform-modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={uploading}>
            {uploading ? (<><RefreshCw size={14} className="spin" /> Uploading...</>) : (<><Upload size={14} /> Upload</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: '#151a23',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '6px',
  padding: '8px 12px',
  fontSize: '13px',
};

export default EntryApkList;
